/**
 * The incident section of the Defender board (L1): the correlated attack
 * stories a SOC actually watches, as aggregates.
 *
 * Reads top-down the way the question is asked — how bad (severity), where it
 * is stuck (status and age), how fast the ones that finished finished (time to
 * close), what it turned out to be (classification and determination), which
 * product raised it and which detector found it (service and detection source,
 * category), and which way the volume is going (trend).
 *
 * Three things that are deliberately not here, and never will be:
 *
 * - **No incident list, and no per-incident drill.** The payload has no field
 *   for an incident's display name, assignee or evidence, because a
 *   Defender-composed incident name routinely contains a device or account
 *   name. There is nothing to drill *to*, so no affordance promises one — the
 *   drill on this section is a chart's table twin, which opens the exact buckets
 *   behind it (§4.4).
 * - **No zeros standing in for refused reads.** Every counter here is
 *   `number | null`. `open_incidents: 0` is genuinely good news; `null` means
 *   the read never answered, and the two never render the same.
 * - **No red light on the time-to-close figure.** It is an upper-bound proxy —
 *   Graph publishes an incident's last *touch*, not a close event — and NOVA
 *   holds no agreed target for it, so toning it as pass or fail would be
 *   asserting both a precision and a target it does not have. The number is
 *   shown, the caveat travels with it in `close_time_caption`, and the colour
 *   stays out of it.
 */

import { Alert, Statistic } from "@norican/react";

import type { DefenderIncidents } from "@/lib/api";

import { DefenderErrorsAlert } from "./access-panel";
import { BandsPanel } from "./bands-panel";
import { BreakdownPanel } from "./breakdown-panel";
import { SectionHeading } from "./panel-card";
import { INCIDENTS_ANCHOR, TWIN_IDS } from "./params";
import {
  formatCount,
  formatDays,
  formatWindow,
  oldestOpenTone,
  problemTone,
  volumeTone,
} from "./status";
import { TrendPanel } from "./trend-panel";

interface IncidentsSectionProps {
  incidents: DefenderIncidents;
}

export function IncidentsSection({ incidents }: IncidentsSectionProps) {
  const windowLabel = formatWindow(incidents.window);
  const staleAfterMs = incidents.cache_ttl_sec * 2000;
  const since = incidents.collected_at;

  return (
    <section id={INCIDENTS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        title="Incidents"
        subtitle={`Correlated attack stories, ${windowLabel}. Counts and mixes only — this board publishes no incident name, assignee or evidence, so there is nothing here that can name a person or a device.`}
      />

      {/* `errors[]` sits above the figures it undermines, and renders nothing
          when the array is empty (§5). */}
      <DefenderErrorsAlert errors={incidents.errors} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Statistic
          label="Open now"
          value={formatCount(incidents.open_incidents)}
          tone={problemTone(incidents.open_incidents)}
          accent
          caption={
            incidents.open_incidents == null
              ? "The read failed — unknown, not zero"
              : incidents.truncated
                ? "At least this many; paging hit NOVA's cap"
                : "Unresolved right now"
          }
        />
        <Statistic
          label="Created in the window"
          value={formatCount(incidents.incidents_total)}
          tone={incidents.incidents_total == null ? "neutral" : "accent"}
          accent
          caption={
            incidents.incidents_total == null
              ? "The read failed — unknown, not zero"
              : `Resolved included, ${windowLabel}`
          }
        />
        <Statistic
          label="Correlated alerts"
          value={formatCount(incidents.correlated_alerts)}
          tone={incidents.correlated_alerts == null ? "neutral" : "accent"}
          accent
          caption={
            incidents.correlated_alerts == null
              ? "The expanded read failed — unknown, not zero"
              : "Alerts Defender correlated into those incidents"
          }
        />
        <Statistic
          label="Oldest open"
          value={formatDays(incidents.oldest_open_days)}
          tone={oldestOpenTone(incidents.open_incidents, incidents.oldest_open_days)}
          accent
          // Null here has two opposite causes, so the caption reads both fields.
          caption={
            incidents.open_incidents == null
              ? "No age was computed, because the read failed"
              : incidents.open_incidents === 0
                ? "Nothing is open, so there is no oldest — a measured absence"
                : incidents.oldest_open_days == null
                  ? "Open incidents carried no usable creation timestamp"
                  : "Age of the longest-unresolved incident"
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BreakdownPanel
          title="Open incidents by severity"
          breakdown={incidents.by_severity}
          form="donut"
          noun="incidents"
          palette="severity"
          ariaLabel={`Open incidents by severity, ${windowLabel}`}
          twinId={TWIN_IDS.incidentSeverity}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          caption={windowLabel}
          totalLabel="Open"
          emptyTitle="No open incidents to break down"
          emptyDescription="The severity read answered and found nothing unresolved. A measured zero, not a refused read — the section above says which."
        />
        <BreakdownPanel
          title="Incidents by status"
          breakdown={incidents.by_status}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by status, ${windowLabel}`}
          twinId={TWIN_IDS.incidentStatus}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          caption={windowLabel}
          emptyTitle="No incidents to break down by status"
          emptyDescription="The status read answered and found no incidents in the window."
        />
        <BreakdownPanel
          title="Incidents by analyst classification"
          breakdown={incidents.by_classification}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by analyst classification, ${windowLabel}`}
          twinId={TWIN_IDS.incidentClassification}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          caption="aggregated behind a k-anonymity floor"
          emptyTitle="Nothing has been classified"
          emptyDescription="No incident in the window carries an analyst classification yet. That is a triage backlog, not a clean estate."
        />
        <BreakdownPanel
          title="Incidents by analyst determination"
          breakdown={incidents.by_determination}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by analyst determination, ${windowLabel}`}
          twinId={TWIN_IDS.incidentDetermination}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          // The board's best available read on alert-tuning debt: what triage
          // concluded, rather than how it was labelled.
          caption="what triage concluded · aggregated behind a k-anonymity floor"
          emptyTitle="Nothing has a determination"
          emptyDescription="No incident in the window has been given a determination yet. That is triage that has not finished, not a set of incidents that turned out to be nothing."
        />
        <BreakdownPanel
          title="Incidents by Defender product"
          breakdown={incidents.by_service_source}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by the Defender product that raised their alerts, ${windowLabel}`}
          twinId={TWIN_IDS.incidentServiceSource}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          caption="aggregated behind a k-anonymity floor"
          emptyTitle="No product attribution to show"
          emptyDescription="No incident in the window named the Defender product its alerts came from."
        />
        <BreakdownPanel
          title="Incidents by detection source"
          breakdown={incidents.by_detection_source}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by the detector behind their correlated alerts, ${windowLabel}`}
          twinId={TWIN_IDS.incidentDetectionSource}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          // These are alerts that were correlated into an incident, so this is
          // also which detectors' alerts actually become incidents — over the
          // incident window, which is longer than the alert panel's.
          caption={`which detectors' alerts became incidents, ${windowLabel} · one incident can carry several · aggregated behind a k-anonymity floor`}
          emptyTitle="No detection source to show"
          emptyDescription="No alert correlated into an incident in this window named the detector that produced it."
        />
        <BreakdownPanel
          title="Incidents by alert category"
          breakdown={incidents.by_category}
          noun="incidents"
          palette="identity"
          ariaLabel={`Incidents by the category of their correlated alerts, ${windowLabel}`}
          twinId={TWIN_IDS.incidentCategory}
          since={since}
          staleAfterMs={staleAfterMs}
          truncated={incidents.truncated}
          caption={`${windowLabel} · one incident can carry several, so these sum above the incident total · aggregated behind a k-anonymity floor`}
          emptyTitle="No category to show"
          emptyDescription="No alert correlated into an incident in this window carried a category. Categories are Microsoft's vocabulary and change without a deploy, so an unfamiliar one is shown as its own bucket rather than folded away."
        />
      </div>

      <BandsPanel
        title="How long open incidents have been open"
        bands={incidents.age_bands}
        noun="open incidents"
        ariaLabel="Open incidents by age band, in whole days since creation"
        twinId={TWIN_IDS.incidentAge}
        since={since}
        staleAfterMs={staleAfterMs}
        truncated={incidents.truncated}
        caption="whole days since creation, oldest band last"
        unknownNote="holds open incidents with no usable creation timestamp."
        emptyTitle="No open incident has an age"
        emptyDescription="Either nothing is open — which the figures above tell you — or no open incident carried a usable creation timestamp. Neither is charted as a zero-height histogram."
      />

      {/* ── How fast the ones that finished finished ────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Statistic
          label="Closed in the window"
          value={formatCount(incidents.closed_incidents)}
          tone={volumeTone(incidents.closed_incidents)}
          accent
          // Published beside the median because it is that median's denominator:
          // a typical time to close over three incidents is not an estate figure.
          caption={
            incidents.closed_incidents == null
              ? "The read failed — unknown, not zero closed"
              : incidents.closed_incidents === 0
                ? "Nothing reached a closed status in the window — so there is no time-to-close to report"
                : `The denominator of the figure beside it, ${windowLabel}`
          }
        />
        <Statistic
          label="Typical time to close"
          value={formatDays(incidents.median_close_days)}
          // Deliberately never toned as pass or fail: an upper-bound proxy with
          // no agreed target must not drive a red light.
          tone="neutral"
          accent
          caption={
            incidents.closed_incidents == null
              ? "The read failed, so nothing was measured — unknown, not immediate"
              : incidents.median_close_days == null
                ? "No closed incident carried a usable pair of timestamps, so no duration could be measured"
                : `Median of ${formatCount(incidents.closed_incidents)} closed incidents · an upper bound, see below`
          }
        />
      </div>

      <BandsPanel
        title="How long closed incidents took"
        bands={incidents.close_time_bands}
        noun="closed incidents"
        ariaLabel="Closed incidents by time to close, in whole days from creation to last update"
        twinId={TWIN_IDS.incidentCloseTime}
        since={since}
        staleAfterMs={staleAfterMs}
        truncated={incidents.truncated}
        caption="whole days from creation to last update, slowest band last"
        unknownNote="holds closed incidents whose timestamps could not be used — never counted as an instant close."
        footnote={
          // The caveat is rendered from the payload verbatim rather than
          // paraphrased here, so the sentence travels with the number wherever
          // the number goes.
          <Alert tone="info" title="This is an upper bound on time to close, not a close time">
            {incidents.close_time_caption}
          </Alert>
        }
        emptyTitle="Nothing closed in this window"
        emptyDescription="No incident in the window reached a closed status, so there is no duration to distribute. Open incidents are excluded entirely rather than counted as a duration of zero — which would report an estate that closes everything instantly."
      />

      <TrendPanel
        title="Incidents created per day"
        points={incidents.created_trend}
        noun="incidents"
        ariaLabel={`Incidents created per UTC day, ${windowLabel}`}
        twinId={TWIN_IDS.incidentTrend}
        since={since}
        staleAfterMs={staleAfterMs}
        truncated={incidents.truncated}
        caption={windowLabel}
        emptyTitle="No incident was created in this window"
        emptyDescription="The window read answered and held no incidents. A measured absence — a flat line at zero would say the same thing far less clearly, so nothing is drawn."
      />
    </section>
  );
}
